import { gql } from "graphql-request";
import { apiSlice } from "../../../store/slices/api/apiSlice";

export const QuestionPaperAuthSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createAssessment: builder.mutation({
      query: ({ access, type }: any) => ({
        url: `/questionpaper/${access}/${type}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    assessmentBulkInvite: builder.query({
      query: ({ scheduleId, remark, data }: any) => ({
        url: `/invitation/api/invite/excel/${scheduleId}/${remark}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getAssessmentByUserId: builder.query({
      query: ({
        pageNumber,
        owner,
        reviewer,
        title,
        status,
        sortKey,
        sortOrder,
        dateFilter = "all",
        customVisibility,
        reviewRequired
      }: any) => ({
        url: "/graphql",
        body: gql`
          query {
            searchQuestionPaper(
              searchFilters: {
              reviewRequired: ${reviewRequired}
              dateFilter: "${dateFilter}"
              indices:["question-paper"]
              stakeHolder:{
                owner: ${owner}
                reviewer: ${reviewer}
              }
                title: "${title}"
                languageList: []
                status: ${status}
                customVisibility: "${customVisibility}"
              }
              pageNumber: ${pageNumber}
              pageSize: 10
              sortKey: "${sortKey}"
              sortOrder: "${sortOrder}"
            ) {
              timestamp
              error
              data {
                searchResult {
                  id
                  org
                  title
                  skills
                  status
                  cloneid
                  version
                  duration
                  language
                  cataloged
                  searchtags {
                    defaultTags
                    bloomTaxonomy
                  }
                  accessibility
                  clonedversion
                  difficultylevel
                  longdescription
                  questionpaperid
                  shortdescription
                  allowedcodinglanguages
                  type
                  access
                  archived
                  sourcename
                  sourcetype
                  templateid
                  stakeholder {
                    owner
                    reviewer
                    observer
                  }
                  reviewrequired
                  versionscreated
                  latestpublishedversion
                  latestdraftversion
                  publishedat
                  publishedby
                  updatedby
                  updatedat
                  createdat
                  createdby
                  expirydate
                  updatedzoneid
                  createdzoneid
                }
                pageNumber
                pageSize
                totalCount
              }
            }
          }
        `
      }),
      extraOptions: { service: "MCQ_Assessment" },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCacheData, responseData, otherArgs) => {
        if (otherArgs.arg.pageNumber > 1) {
          const messageMap = new Map();
          [
            ...currentCacheData.searchQuestionPaper.data.searchResult,
            ...responseData.searchQuestionPaper.data.searchResult
          ].map((msg) => {
            if (!messageMap.has(msg.id)) {
              messageMap.set(msg.id, msg);
            }
          });
          const mergedResult = [...messageMap.values()];
          return {
            ...responseData,
            searchQuestionPaper: {
              ...responseData.searchQuestionPaper,
              data: {
                ...responseData.searchQuestionPaper.data,
                searchResult: mergedResult
              }
            }
          };
        } else return responseData;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      }
    }),
    getQuestionPaperById: builder.query({
      query: ({ id, version }: any) => ({
        url: "/graphql",
        body: gql`
          query{
  getQuestionPaperDetailsQL(
    version: ${version}
    questionpaperid: "${id}"
  ){
    data{
      questionPaperId
      title
      version
      shortDescription
      status
      duration
      longDescription
      searchTags{defaultTags
      bloomTaxonomy
      contentUsage
      contentRegion
      contentDomain
      contentCategory
      contentSubCategory}
      skills
      difficultyLevel
      language
      allowedCodingLanguages
      latestPublishedVersion
      accessibility
      access
      cloneId
      clonedVersion
      expiryDate
      totalMarks
      totalQuestions
      cataloged
      questionTypeCountMap
      org
      createdat
      createdby
      stakeholder{owner observer reviewer}
      sourceName
      sourceType
      versionsCreated
      reviewRequired
      
    }
  }
}
        `
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateQuestionPaper: builder.mutation({
      query: ({ id, version, payload }: any) => ({
        url: `/questionpaper/${id}/${version}`,
        method: "PUT",
        body: payload
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateReviewRequired: builder.mutation({
      query: ({ id, reviewrequired }: any) => ({
        url: `/questionpaper/version-details/${id}`,
        method: "PUT",
        params: { reviewrequired }
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    validateQp: builder.query({
      query: ({ questionpaperid, version, status }: any) => ({
        url: `/questionpaper/${questionpaperid}/${version}/${status}`,
        method: "GET"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateQpStatus: builder.mutation({
      query: ({ id, version, payload }: any) => ({
        url: `/questionpaper/status/${id}/${version}`,
        method: "PUT",
        body: payload
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    cloneQp: builder.mutation({
      query: ({ id, version, payload }: any) => ({
        url: `/questionpaper/clone/${id}/${version}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    qpNewVersion: builder.mutation({
      query: ({ id, version, payload }: any) => ({
        url: `/questionpaper/version/${id}/${version}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateQpStakeholders: builder.mutation({
      query: ({ id, payload }: any) => ({
        url: `/questionpaper/version-details/${id}`,
        method: "POST",
        body: payload
      }),
      extraOptions: { service: "MCQ_Assessment" }
    })
  })
});

export const {
  useCreateAssessmentMutation,
  useGetAssessmentByUserIdQuery,
  useLazyGetAssessmentByUserIdQuery,
  useGetQuestionPaperByIdQuery,
  useLazyGetQuestionPaperByIdQuery,
  useUpdateQuestionPaperMutation,
  useUpdateReviewRequiredMutation,
  useLazyValidateQpQuery,
  useUpdateQpStatusMutation,
  useCloneQpMutation,
  useUpdateQpStakeholdersMutation,
  useQpNewVersionMutation,
  useAssessmentBulkInviteQuery,
  useLazyAssessmentBulkInviteQuery
} = QuestionPaperAuthSlice;
